import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Clapperboard, Film, Music } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { mediaPlayback, mediaPosterFromFrame } from '@/shared/api/generated/media/media';
import type { AssetBody } from '@/shared/api/generated/model';
import { apiPath } from '@/shared/api/binary';
import { ApiError } from '@/shared/api/http';
import { cx } from '@/shared/lib/cx';
import { AuthImage } from '@/shared/ui/AuthImage';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Slider } from '@/shared/ui/Slider';
import { toast } from '@/shared/ui/Toast';
import { VideoPlayer } from '@/shared/ui/VideoPlayer';

import styles from './VideoTools.module.scss';

/**
 * Просмотр видео и аудио в медиатеке и постер из кадра.
 *
 * Тег `<video>` не умеет послать токен, поэтому сервер выдаёт подписанную
 * ссылку на несколько часов (mediaPlayback) — с ней видео играет сразу и
 * с перемоткой, не скачиваясь целиком. По той же подписи сервер отдаёт
 * кадры: из них раскадровка, и она работает, даже если браузер сам формат
 * не показывает (HEVC из iPhone в Chrome, например).
 */

/** Шаг «на кадр»: при 25 кадрах в секунду — один кадр. */
const FRAME_STEP = 0.04;

interface Playback {
  url: string;
  frame: (at: number, width: number) => string;
}

function usePlayback(asset: AssetBody) {
  return useQuery({
    queryKey: ['media-playback', asset.id],
    // Ссылка живёт 5–6 часов; через час берём новую заранее.
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<Playback> => {
      const response = await mediaPlayback(asset.id);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос ссылки');
      }

      const { url, frame_url: frameURL } = response.data;

      return {
        url: apiPath(url),
        frame: (at, width) =>
          apiPath(`${frameURL}&at=${at.toFixed(2)}&w=${String(Math.round(width))}`),
      };
    },
  });
}

/** Превью видео или аудио в карточке файла — сразу проигрыватель. */
export function MediaPlayer({ asset }: { asset: AssetBody }) {
  const playback = usePlayback(asset);
  const poster = usePosterURL(asset);
  const [broken, setBroken] = useState(false);

  if (playback.data === undefined || broken) {
    const Icon = asset.kind === 'audio' ? Music : Film;

    return (
      <div className={styles['player']}>
        <div className={styles['player__empty']}>
          <Icon size={32} />
          {broken && (
            <span>
              Браузер не умеет показать этот формат.
              {asset.kind === 'video' &&
                asset.hls_status === 'ready' &&
                ' На сайте видео играет из HLS.'}
            </span>
          )}
        </div>
      </div>
    );
  }

  const frame = playback.data.frame;

  return (
    <VideoPlayer
      // Ссылка обновилась — новый элемент, а не смена src посреди просмотра.
      key={playback.data.url}
      kind={asset.kind === 'audio' ? 'audio' : 'video'}
      className={asset.kind === 'audio' ? undefined : styles['player']}
      src={playback.data.url}
      {...(poster === undefined ? {} : { poster })}
      preview={(at) => frame(at, 320)}
      onError={() => {
        setBroken(true);
      }}
    />
  );
}

/**
 * Постер для проигрывателя — превью файла (выбранный постер или кадр
 * первой секунды): адрес подписан, браузер берёт его без токена.
 */
function usePosterURL(asset: AssetBody): string | undefined {
  return asset.kind === 'video' && asset.thumb_url != null ? apiPath(asset.thumb_url) : undefined;
}

/**
 * Постер видео: что сейчас стоит, кадр из самого видео (первый — в один
 * щелчок, любой — в окне с раскадровкой) или картинка из медиатеки.
 */
export function PosterSection({
  asset,
  onChanged,
  onPickFromLibrary,
  onClear,
}: {
  asset: AssetBody;
  onChanged: () => void;
  onPickFromLibrary: () => void;
  onClear: () => void;
}) {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  const takeFirst = () => {
    setBusy(true);
    makePoster(asset, 0)
      .then(onChanged)
      .catch(() => {
        // Ошибку уже показал makePoster.
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className={styles['poster']}>
      <span className={styles['label']}>Постер</span>
      <p className={styles['hint']}>
        Картинка, которую видно до запуска видео. Кадр сохраняется в медиатеку отдельной картинкой
        рядом с видео.
      </p>

      {asset.poster_id != null && (
        <div className={styles['poster__current']}>
          <AuthImage
            src={`/media/${asset.poster_id}/thumb`}
            alt="Постер"
            className={styles['poster__image']}
          />
        </div>
      )}

      <div className={styles['actions']}>
        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          iconLeft={<Clapperboard size={14} />}
          onClick={() => {
            setPicking(true);
          }}
        >
          Кадр из видео
        </Button>
        <Button size="sm" variant="outline" tone="neutral" loading={busy} onClick={takeFirst}>
          Первый кадр
        </Button>
        <Button size="sm" variant="ghost" tone="neutral" onClick={onPickFromLibrary}>
          Из медиатеки
        </Button>
        {asset.poster_id != null && (
          <Button size="sm" variant="ghost" tone="neutral" onClick={onClear}>
            Снять
          </Button>
        )}
      </div>

      <Dialog open={picking} onOpenChange={setPicking}>
        {picking && (
          <FramePicker
            asset={asset}
            onDone={() => {
              setPicking(false);
              onChanged();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

async function makePoster(asset: AssetBody, at: number): Promise<void> {
  try {
    const response = await mediaPosterFromFrame(asset.id, { at });

    if (response.status !== 200) {
      throw new Error('Неожиданный ответ');
    }

    toast.success('Постер готов', {
      description: `Кадр сохранён в медиатеку: «${response.data.poster.name}».`,
    });
  } catch (cause) {
    toast.error('Не удалось сделать постер', {
      description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
    });

    throw cause;
  }
}

/**
 * Окно выбора кадра: видео с перемоткой, лента кадров для быстрого прыжка и
 * шаг «на кадр» для точной подгонки. Если браузер видео не показывает —
 * вместо него кадр от сервера и ползунок по времени.
 */
function FramePicker({ asset, onDone }: { asset: AssetBody; onDone: () => void }) {
  const playback = usePlayback(asset);
  const video = useRef<HTMLMediaElement>(null);
  const [time, setTime] = useState(0);
  const [length, setLength] = useState(asset.duration ?? 0);
  const [broken, setBroken] = useState(false);
  const [saving, setSaving] = useState(false);

  const seek = (at: number) => {
    const next = Math.min(Math.max(0, at), Math.max(0, length - 0.05));

    setTime(next);

    if (video.current !== null) {
      video.current.pause();
      video.current.currentTime = next;
    }
  };

  const save = () => {
    setSaving(true);
    makePoster(asset, time)
      .then(onDone)
      .catch(() => {
        setSaving(false);
      });
  };

  const frame = playback.data?.frame;

  return (
    <DialogContent
      title="Постер из кадра"
      description="Остановите видео на нужном месте или выберите кадр в ленте. Стрелки — шаг на кадр."
      size="lg"
      footer={
        <Button loading={saving} disabled={playback.data === undefined} onClick={save}>
          Сделать постером · {timecode(time)}
        </Button>
      }
    >
      <div
        className={styles['picker']}
        onKeyDown={(event) => {
          // Стрелки — шаг на кадр, где бы ни был фокус в окне. Плеер в
          // фокусе обрабатывает их сам (seekStep) и дальше не пускает.
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

          event.preventDefault();
          seek(time + (event.key === 'ArrowLeft' ? -FRAME_STEP : FRAME_STEP));
        }}
      >
        {playback.data === undefined || frame === undefined ? (
          <div className={styles['stage']} />
        ) : broken ? (
          <div className={styles['stage']}>
            <img
              className={styles['stage__media']}
              src={frame(time, 960)}
              alt={`Кадр ${timecode(time)}`}
            />
          </div>
        ) : (
          <VideoPlayer
            className={styles['stage']}
            src={playback.data.url}
            preload="auto"
            seekStep={FRAME_STEP}
            autoFocus
            mediaRef={video}
            preview={(at) => frame(at, 320)}
            onTimeUpdate={setTime}
            onDuration={setLength}
            onError={() => {
              setBroken(true);
            }}
          />
        )}

        {broken && length > 0 && (
          <Slider
            aria-label="Секунда видео"
            min={0}
            max={length}
            step={0.1}
            value={time}
            onValueChange={seek}
          />
        )}

        <div className={styles['step']}>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            iconLeft={<ChevronLeft size={14} />}
            onClick={() => {
              seek(time - FRAME_STEP);
            }}
          >
            Кадр назад
          </Button>
          <span className={styles['time']}>{timecode(time, 2)}</span>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            iconRight={<ChevronRight size={14} />}
            onClick={() => {
              seek(time + FRAME_STEP);
            }}
          >
            Кадр вперёд
          </Button>
        </div>

        {frame !== undefined && length > 0 && (
          <FrameStrip length={length} time={time} frame={frame} onSeek={seek} />
        )}
      </div>
    </DialogContent>
  );
}

/**
 * Шаг ленты кадров: у короткого ролика — каждые полсекунды, у длинного —
 * реже, но не больше ~150 кадров: каждый — запрос к ffmpeg на сервере.
 */
function stripInterval(length: number): number {
  if (length <= 20) return 0.5;
  if (length <= 60) return 1;
  if (length <= 300) return 2;

  return length / 150;
}

/**
 * Лента кадров: прокручивается (колесом, тачпадом, стрелками по краям) и
 * сама едет за текущим кадром. Картинки грузятся, только когда видны.
 */
function FrameStrip({
  length,
  time,
  frame,
  onSeek,
}: {
  length: number;
  time: number;
  frame: (at: number, width: number) => string;
  onSeek: (at: number) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const interval = stripInterval(length);
  const times = Array.from({ length: Math.floor(length / interval) + 1 }, (_, i) =>
    Math.min(i * interval, Math.max(0, length - 0.05)),
  );
  const current = Math.min(times.length - 1, Math.round(time / interval));

  // Колесо мыши крутит ленту вбок: вертикальной прокрутки у неё нет.
  // Слушатель — не через React: там он пассивный и preventDefault не работает.
  useEffect(() => {
    const el = scroller.current;

    if (el === null) return;

    const wheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      event.preventDefault();
      el.scrollLeft += event.deltaY;
    };

    el.addEventListener('wheel', wheel, { passive: false });

    return () => {
      el.removeEventListener('wheel', wheel);
    };
  }, []);

  // Текущий кадр — всегда в поле зрения.
  useEffect(() => {
    const el = scroller.current?.children[current];

    if (el instanceof HTMLElement) {
      el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [current]);

  const page = (direction: -1 | 1) => {
    const el = scroller.current;

    el?.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <div className={styles['strip']}>
      <button
        type="button"
        className={styles['strip__nav']}
        aria-label="Раньше"
        onClick={() => {
          page(-1);
        }}
      >
        <ChevronLeft size={18} />
      </button>

      <div
        ref={scroller}
        className={styles['strip__scroller']}
        role="list"
        aria-label="Лента кадров"
      >
        {times.map((at, index) => (
          <button
            key={at}
            type="button"
            role="listitem"
            className={cx(
              styles['strip__frame'],
              index === current && styles['strip__frame--current'],
            )}
            onClick={() => {
              onSeek(at);
            }}
          >
            <img src={frame(at, 240)} alt="" loading="lazy" />
            <span>{timecode(at, interval < 1 ? 1 : 0)}</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className={styles['strip__nav']}
        aria-label="Позже"
        onClick={() => {
          page(1);
        }}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

/** 0:03, 1:27; с долями — 0:03.5 (digits 1) или 0:03.40 (digits 2). */
function timecode(seconds: number, digits: 0 | 1 | 2 = 0): string {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  const whole =
    digits === 0
      ? String(Math.floor(s)).padStart(2, '0')
      : (Math.floor(s * 10 ** digits) / 10 ** digits).toFixed(digits).padStart(3 + digits, '0');

  return `${String(m)}:${whole}`;
}
